/**
 * WP-231 web-push subscription flow behind a port (`BrowserPushPort`): the real adapter wraps
 * `Notification` + `PushManager`; tests use `createFakePushPort`. Denied permission is recorded
 * once (`deniedNoticeSeen`) so the explanatory prompt is never repeated.
 */
export type PushPermission = "default" | "granted" | "denied";

export interface PushSubscriptionJson {
  readonly endpoint: string;
  readonly keys?: Readonly<Record<string, string>>;
}

export interface BrowserPushPort {
  supported(): boolean;
  permission(): PushPermission;
  requestPermission(): Promise<PushPermission>;
  subscribe(vapidPublicKey: string): Promise<PushSubscriptionJson>;
}

export interface PushStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

const DENIED_SEEN = "wato.push.deniedNoticeSeen";

/** Set to "1" when the user hides the informational push note in the account menu. */
export const PUSH_HINT_DISMISSED_KEY = "wato.push.hintDismissed";

export const memoryPushStore = (): PushStore => {
  const m = new Map<string, string>();
  return { get: (k) => m.get(k) ?? null, set: (k, v) => void m.set(k, v) };
};

export const localPushStore = (): PushStore => ({
  get: (k) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k, v) => {
    try {
      localStorage.setItem(k, v);
    } catch {
      /* storage unavailable: the prompt may repeat only within this session */
    }
  },
});

export type EnablePushResult =
  | { readonly status: "subscribed" }
  | { readonly status: "unsupported" }
  /** `showNotice` is true exactly once; afterwards the in-app unread badge is the only fallback. */
  | { readonly status: "denied"; readonly showNotice: boolean };

export async function enablePush(input: {
  port: BrowserPushPort;
  store: PushStore;
  vapidPublicKey: string;
  /** Sends the subscription to `POST /v1/notifications/push-subscribe`. */
  register: (sub: PushSubscriptionJson) => Promise<void>;
}): Promise<EnablePushResult> {
  const { port, store } = input;
  if (!port.supported() || input.vapidPublicKey === "") return { status: "unsupported" };
  const permission =
    port.permission() === "default" ? await port.requestPermission() : port.permission();
  if (permission !== "granted") {
    const showNotice = store.get(DENIED_SEEN) !== "1";
    if (showNotice) store.set(DENIED_SEEN, "1");
    return { status: "denied", showNotice };
  }
  await input.register(await port.subscribe(input.vapidPublicKey));
  return { status: "subscribed" };
}

export function createFakePushPort(
  initial: PushPermission,
  onRequest: PushPermission = "granted",
): BrowserPushPort & { readonly subscribed: string[] } {
  let current = initial;
  const subscribed: string[] = [];
  return {
    subscribed,
    supported: () => true,
    permission: () => current,
    requestPermission: async () => {
      current = onRequest;
      return current;
    },
    subscribe: async (key) => {
      subscribed.push(key);
      return { endpoint: "https://push.example/fake", keys: { p256dh: "k", auth: "a" } };
    },
  };
}

export function createBrowserPushPort(swUrl = "/service-worker.js"): BrowserPushPort {
  return {
    supported: () =>
      typeof Notification !== "undefined" &&
      "serviceWorker" in navigator &&
      "PushManager" in window,
    permission: () => Notification.permission,
    requestPermission: () => Notification.requestPermission(),
    subscribe: async (vapidPublicKey) => {
      const reg =
        (await navigator.serviceWorker.getRegistration()) ??
        (await navigator.serviceWorker.register(swUrl));
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      });
      return sub.toJSON() as PushSubscriptionJson;
    },
  };
}
